import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useContextSite } from "../../../context/Context";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { RolesEnum } from "../../../enums/roles";
import { Tracking } from "../../../services/Tracking";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import {
  IGetAgendamentosProps,
  IIniciarAgendamentoProps,
} from "../../../types/agendamento";
import { IPagination } from "../../../types/pagination";
import { useAgendamentosLista } from "../../../hooks/queries/agendamento/useAgendamentosLista/useAgendamentosLista";
import { useAtribuirAgendamento } from "../../../hooks/queries/agendamento/useAtribuirAgendamento/useAtribuirAgendamento";
import { useIniciarVistoria } from "../../../hooks/queries/agendamento/useIniciarVistoria/useIniciarVistoria";
import { useColaboradorAtual } from "../../../hooks/queries/colaborador/useColaboradorAtual/useColaboradorAtual";

export const useSchedulesV2 = () => {
  const navigate = useNavigate();
  const { setIsLoad } = useContextSite();
  const { hasRole, isUserType, userStorageData } = useUserPermissions();

  const isCliente = hasRole(RolesEnum.ROLE_CLIENTE);
  const isCustomsBroker = isUserType("DESPACHANTE");
  const novoAgendamentoRoute = isCustomsBroker
    ? "/agendamento/buscar-cliente"
    : "/agendamento";

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [numberPage, setNumberPage] = useState(0);
  const [filtro, setFiltro] = useState<IGetAgendamentosProps>({});

  const idCliente = isCliente ? userStorageData?.uuidCliente : undefined;

  const {
    data: agendamentosPagina,
    isLoading,
    isError,
    error,
    refetch,
  } = useAgendamentosLista({
    ...filtro,
    idCliente,
    page: numberPage,
    size: DEFAULT_PAGE_SIZE,
  });

  const agendamentos = agendamentosPagina?.content || [];
  const pagination: IPagination = {
    actualPage: agendamentosPagina?.number ?? 0,
    totalPage: agendamentosPagina?.totalPages ?? 0,
    totalRegister: agendamentosPagina?.totalElements ?? 0,
  };

  const { data: colaboradorAtual } = useColaboradorAtual(!isCliente);
  const { mutate: atribuirAgendamentoMutate } = useAtribuirAgendamento();
  const { mutate: iniciarVistoriaMutate } = useIniciarVistoria();

  function handleToggleFilter() {
    setIsFilterOpen(true);
    Tracking.userAction("mapa:meus-agendamentos:clique-filtrar");
  }

  function handleCloseFilter() {
    setIsFilterOpen(false);
  }

  function handleFilter(data: IGetAgendamentosProps) {
    setFiltro(data);
    setNumberPage(0);
    Tracking.userAction("mapa:meus-agendamentos:aplicar-filtro");
  }

  function handleClearFilter() {
    setFiltro({});
    setNumberPage(0);
    setIsFilterOpen(false);
  }

  function handleNovoAgendamento() {
    navigate(novoAgendamentoRoute);
    Tracking.userAction("mapa:meus-agendamentos:clique-novo-agendamento");
  }

  function atribuirAgendamento(data: IIniciarAgendamentoProps) {
    setIsLoad(true);

    atribuirAgendamentoMutate(
      { ...data, uuidAtendente: colaboradorAtual?.uuid },
      {
        onSuccess: () => {
          toast.success("Agendamento atribuido com sucesso!");
          refetch();
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error("mapa:meus-agendamentos:atribuir-falhou", {
            mensagem,
          });
        },
        onSettled: () => setIsLoad(false),
      },
    );
  }

  function iniciarVistoria(data: IIniciarAgendamentoProps) {
    setIsLoad(true);

    iniciarVistoriaMutate(
      { ...data, uuidAtendente: colaboradorAtual?.uuid },
      {
        onSuccess: () => {
          toast.success("Agendamento iniciado");
          refetch();
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error("mapa:meus-agendamentos:iniciar-falhou", {
            mensagem,
          });
        },
        onSettled: () => setIsLoad(false),
      },
    );
  }

  useEffect(() => {
    if (!isError) return;

    const {
      response: {
        data: { mensagem },
      },
    } = error as any;

    Tracking.error("mapa:meus-agendamentos:buscar-agendamentos-falhou", {
      mensagem,
    });
  }, [isError]);

  useEffect(() => {
    setIsLoad(isLoading);
  }, [isLoading]);

  return {
    isCliente,
    isFilterOpen,
    handleToggleFilter,
    handleCloseFilter,
    handleFilter,
    handleClearFilter,
    handleNovoAgendamento,
    agendamentos,
    pagination,
    numberPage,
    setNumberPage,
    atribuirAgendamento,
    iniciarVistoria,
  };
};
